<script setup>
import moment from 'moment'
import { useAbility } from '@casl/vue'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

import { cookieRef } from '@/@layouts/stores/config'

import CommitteeService from '@/services/quality/committee.service'
import { MeetingStatus, meetingStatusColor, meetingStatusTitle } from '@/enums/quality/meeting-status'
import { MinutesStatus, minutesStatusColor, minutesStatusTitle } from '@/enums/quality/minutes-status'
import { meetingModeColor, meetingModeTitle } from '@/enums/quality/meeting-mode'
import MeetingCreateDrawer from '@/views/quality/committee/MeetingCreateDrawer.vue'

definePage({
  meta: {
    subject: 'committee.menu',
    action: 'meetings',
  },
})

const { can } = useAbility()

// ── Filter options ───────────────────────────────────
const statusOptions = Object.values(MeetingStatus).map(value => ({ title: meetingStatusTitle(value), value }))
const minutesStatusOptions = Object.values(MinutesStatus).map(value => ({ title: minutesStatusTitle(value), value }))

// Committees — for the filter select, scoped to the selected unit like the
// committee list page's own filters.
const selectedUnitId = cookieRef('selectedUnit')

const committees = ref([])

CommitteeService.list({ per_page: 200, unit_id: selectedUnitId.value }, data => {
  committees.value = data?.data ?? data ?? []
})

// ── Filters state ────────────────────────────────────
const committeeId = ref(null)
const status = ref(null)
const minutesStatus = ref(null)
const searchQuery = ref('')

// Date range (single field, range mode) — mirrors the HAI surveillance
// list's convention rather than two separate pickers.
const dateRange = ref(null)
const fromDate = ref(null)
const toDate = ref(null)

const dateRangeConfig = ref({
  mode: 'range',
  dateFormat: 'Y-m-d',
  enableTime: false,
  onChange: selectedDates => {
    if (selectedDates.length > 0) {
      fromDate.value = moment(selectedDates[0]).format('YYYY-MM-DD')
      toDate.value = selectedDates[1]
        ? moment(selectedDates[1]).format('YYYY-MM-DD')
        : null
    } else {
      fromDate.value = null
      toDate.value = null
    }
  },
})

const debouncedSearch = useDebounceFn(val => {
  searchQuery.value = val
  page.value = 1
}, 500)

const filters = computed(() => ({
  committeeId: committeeId.value,
  status: status.value,
  minutesStatus: minutesStatus.value,
  from: fromDate.value,
  to: toDate.value,
  search: searchQuery.value,
}))

// ── Table state ──────────────────────────────────────
const itemsPerPage = ref(10)
const page = ref(1)

// The meetings index orders by orderByDesc(DB::raw(CommitteeMeeting::DATE_EXPRESSION))
// — COALESCE(held_at, scheduled_at) — and accepts no sort params, so every
// column is non-sortable rather than exposing sort controls that would
// silently do nothing.
const headers = [
  { title: 'Code', key: 'code', sortable: false },
  { title: 'Committee', key: 'committee_name', sortable: false },
  { title: 'Scheduled', key: 'scheduled_at', sortable: false },
  { title: 'Venue', key: 'venue', sortable: false },
  { title: 'Mode', key: 'mode', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Minutes', key: 'minutes_status', sortable: false },
  { title: 'Quorum', key: 'quorum_met', sortable: false },
]

// quorum_met is three-state: null covers several genuinely different cases
// (not yet evaluated, no rule declared, or evaluated with no verdict — see
// MeetingHeader.vue's quorumUnknownTooltip) and must render as neutral,
// never as a failure. false is 'warning' (a recorded fact), not 'error',
// matching ADJOURNED_NO_QUORUM's own colour in meeting-status.js.
const quorumChip = quorumMet => {
  if (quorumMet === true) return { text: 'Quorate', color: 'success', variant: 'flat' }
  if (quorumMet === false) return { text: 'Not Quorate', color: 'warning', variant: 'flat' }

  return { text: 'Not evaluated', color: 'secondary', variant: 'outlined' }
}

const meetings = ref([])
const totalMeetings = ref(0)
const isFetching = ref(false)

const buildQuery = () => ({
  committee_id: committeeId.value || undefined,
  status: status.value?.length ? status.value : undefined,
  minutes_status: minutesStatus.value?.length ? minutesStatus.value : undefined,
  from: fromDate.value || undefined,
  to: toDate.value || undefined,
  search: searchQuery.value || undefined,
  per_page: itemsPerPage.value,
  page: page.value,
})

// Monotonic token guarding against out-of-order responses: filter changes,
// search and pagination each fire an independent request with no
// cancellation, so a slow earlier response could otherwise land after a
// faster later one and silently overwrite correct rows with stale ones.
let fetchToken = 0

const fetchMeetings = async () => {
  const token = ++fetchToken

  isFetching.value = true
  await CommitteeService.listMeetings(buildQuery(), data => {
    // Ignore a response a newer request has already superseded.
    if (token !== fetchToken) return
    meetings.value = data?.data ?? []
    totalMeetings.value = data?.meta?.total ?? 0
  })
  if (token === fetchToken) isFetching.value = false
}

// Any filter change (including debounced search) resets to page 1. If we're
// already on page 1 that reset is a no-op, so fetch directly; otherwise let
// the page watcher below fire the fetch once the reset lands.
watch(filters, () => {
  if (page.value === 1)
    fetchMeetings()
  else
    page.value = 1
})

watch([page, itemsPerPage], fetchMeetings)

fetchMeetings()

const router = useRouter()

const goToMeeting = item => {
  router.push({ name: 'app-quality-committee-meetings-id', params: { id: item.id } })
}

// ── Add Meeting ──────────────────────────────────────
const isCreateDrawerVisible = ref(false)

const onMeetingSaved = () => {
  fetchMeetings()
}
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText class="d-flex flex-wrap gap-4 align-center">
        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 12rem;"
        >
          <AppSelect
            v-model="committeeId"
            :items="committees"
            item-title="name"
            item-value="id"
            placeholder="Committee"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 14rem;"
        >
          <AppSelect
            v-model="status"
            :items="statusOptions"
            item-title="title"
            item-value="value"
            placeholder="Status"
            density="compact"
            multiple
            chips
            closable-chips
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 14rem;"
        >
          <AppSelect
            v-model="minutesStatus"
            :items="minutesStatusOptions"
            item-title="title"
            item-value="value"
            placeholder="Minutes Status"
            density="compact"
            multiple
            chips
            closable-chips
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 14rem;"
        >
          <AppDateTimePicker
            v-model="dateRange"
            placeholder="Date Range"
            density="compact"
            :config="dateRangeConfig"
            clearable
          />
        </div>
      </VCardText>
    </VCard>

    <VCard
      variant="flat"
      class="border"
    >
      <VCardItem>
        <template #title>
          <AppTextField
            placeholder="Search..."
            density="compact"
            style="inline-size: 280px;"
            @input="debouncedSearch($event.target.value)"
          >
            <template #prepend-inner>
              <VIcon
                icon="tabler-search"
                size="16"
              />
            </template>
          </AppTextField>
        </template>
        <template #append>
          <div class="d-flex align-center gap-3">
            <VBtn
              v-if="can('schedule', 'quality.committee')"
              color="primary"
              variant="flat"
              prepend-icon="tabler-plus"
              @click="isCreateDrawerVisible = true"
            >
              Add Meeting
            </VBtn>

            <IconBtn
              variant="tonal"
              class="rounded"
              :loading="isFetching"
              @click="fetchMeetings"
            >
              <VIcon icon="tabler-refresh" />
              <VTooltip activator="parent">
                Refresh
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VCardItem>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :items="meetings"
        :items-length="totalMeetings"
        :loading="isFetching"
        class="text-no-wrap"
        hover
        @click:row="(_, { item }) => goToMeeting(item)"
      >
        <!-- Committee -->
        <template #item.committee_name="{ item }">
          {{ item.committee_name ?? '—' }}
        </template>

        <!--
          Scheduled: held_at once the meeting has occurred, else
          scheduled_at — same COALESCE the server orders by. Showing
          scheduled_at alone would bury a meeting held on a date other
          than the one originally scheduled mid-list with no date shown.
        -->
        <template #item.scheduled_at="{ item }">
          <VChip
            v-if="!item.held_at && !item.scheduled_at"
            variant="outlined"
            size="small"
            label
          >
            Ad hoc
          </VChip>
          <span v-else>{{ formatDate(item.held_at ?? item.scheduled_at) }}</span>
        </template>

        <!-- Venue -->
        <template #item.venue="{ item }">
          {{ item.venue ?? '—' }}
        </template>

        <!-- Mode -->
        <template #item.mode="{ item }">
          <VChip
            v-if="item.mode"
            :color="meetingModeColor(item.mode)"
            size="small"
            label
          >
            {{ meetingModeTitle(item.mode) }}
          </VChip>
          <span v-else>—</span>
        </template>

        <!-- Status -->
        <template #item.status="{ item }">
          <VChip
            :color="meetingStatusColor(item.status)"
            size="small"
            label
          >
            {{ meetingStatusTitle(item.status) }}
          </VChip>
        </template>

        <!-- Minutes -->
        <template #item.minutes_status="{ item }">
          <VChip
            :color="minutesStatusColor(item.minutes_status)"
            size="small"
            label
          >
            {{ minutesStatusTitle(item.minutes_status) }}
          </VChip>
        </template>

        <!-- Quorum -->
        <template #item.quorum_met="{ item }">
          <VChip
            :color="quorumChip(item.quorum_met).color"
            :variant="quorumChip(item.quorum_met).variant"
            size="small"
            label
          >
            {{ quorumChip(item.quorum_met).text }}
          </VChip>
        </template>

        <!-- Bottom -->
        <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
            <p class="text-sm text-medium-emphasis mb-0">
              {{ totalMeetings }} total records
            </p>
            <VPagination
              v-model="page"
              :length="Math.ceil(totalMeetings / itemsPerPage)"
              :total-visible="5"
            />
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <MeetingCreateDrawer
      v-model:is-drawer-open="isCreateDrawerVisible"
      :committee-id="null"
      @saved="onMeetingSaved"
    />
  </section>
</template>
